import React, { useEffect, useRef, useState } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useDelayedFlag } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import AuthLayout, { AuthHeading, PasswordField } from './auth-parts/AuthLayout';

const LINK_INVALID = 'This reset link has expired or was already used.';
const LINK_UNOPENABLE = 'This reset link couldn’t be opened.';
const PASSWORD_HINT = 'At least 8 characters.';
const PASSWORD_TOO_SHORT = 'This password is too short. Use at least 8 characters.';
const PASSWORDS_DONT_MATCH = 'These don’t match. Re-enter the new password here.';
const PASSWORD_UNCHANGED = 'This is your current password. Choose a different one.';
const PASSWORD_WEAK = 'This password is too easy to guess. Make it longer, with a mix of letters, numbers and symbols.';
const PASSWORD_SAVED = 'Password saved. Signing you in…';

/** What went wrong with the last attempt. Field kinds render on their field. */
type ResetIssue =
  | { kind: 'tooShort' }
  | { kind: 'mismatch' }
  | { kind: 'unchanged' }
  | { kind: 'weak' }
  | { kind: 'failed' };

const NEW_PASSWORD_ISSUES: Partial<Record<ResetIssue['kind'], string>> = {
  tooShort: PASSWORD_TOO_SHORT,
  unchanged: PASSWORD_UNCHANGED,
  weak: PASSWORD_WEAK,
};

/** A missing or expired recovery session: the link can't be used, whatever is typed. */
const isSessionMissing = (err: any) =>
  err?.name === 'AuthSessionMissingError' || /auth session missing/i.test(err?.message ?? '');

/** Presentation only: maps an updateUser failure to what the form shows. */
const describeUpdateFailure = (err: any): ResetIssue => {
  const message: string = err?.message ?? '';
  if (err?.code === 'same_password' || /should be different/i.test(message)) return { kind: 'unchanged' };
  if (err?.code === 'weak_password' || err?.name === 'AuthWeakPasswordError') return { kind: 'weak' };
  return { kind: 'failed' };
};

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
  p: 0,
  m: '-1px',
} as const;

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [issue, setIssue] = useState<ResetIssue | null>(null);
  // Set when there's no recovery session: the link is a dead end, whatever is typed.
  const [linkProblem, setLinkProblem] = useState<null | 'expired' | 'unopenable'>(null);
  const linkInvalid = linkProblem !== null;
  const [accountEmail, setAccountEmail] = useState('');
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const requestLinkRef = useRef<HTMLAnchorElement>(null);
  const focusRequestLink = useRef(false);
  const checkingLink = useDelayedFlag(!ready, 300);

  useEffect(() => {
    const init = async () => {
      try {
        const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
        const params = new URLSearchParams(hash);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');

        if (accessToken && refreshToken) {
          await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        }

        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          setLinkProblem('expired');
        } else {
          setAccountEmail(data.session.user?.email ?? '');
        }
      } catch {
        // The link couldn't be opened: the same dead end as an expired link.
        setLinkProblem('unopenable');
      } finally {
        setReady(true);
      }
    };

    init();
  }, []);

  const focusNewPassword = () => {
    passwordRef.current?.focus();
    passwordRef.current?.select();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (linkInvalid || !ready || loading || saved) return;
    setIssue(null);

    // Validation errors belong to a field; move focus there so the fix is one keystroke away.
    if (password.length < 8) {
      setIssue({ kind: 'tooShort' });
      focusNewPassword();
      return;
    }
    if (password !== confirmPassword) {
      setIssue({ kind: 'mismatch' });
      confirmRef.current?.focus();
      confirmRef.current?.select();
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        throw updateError;
      }
      setSaved(true);
      // '/' resolves through the role-aware DefaultRedirect; employees cannot
      // open /dashboard directly.
      setTimeout(() => navigate('/', { replace: true }), 1200);
    } catch (err: any) {
      if (isSessionMissing(err)) {
        // The form goes away; hand focus to the one way forward.
        focusRequestLink.current = true;
        setLinkProblem('expired');
      } else {
        const next = describeUpdateFailure(err);
        setIssue(next);
        if (next.kind !== 'failed') focusNewPassword();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (linkProblem && focusRequestLink.current) {
      focusRequestLink.current = false;
      requestLinkRef.current?.focus();
    }
  }, [linkProblem]);

  const newPasswordError = issue ? NEW_PASSWORD_ISSUES[issue.kind] : undefined;
  const confirmError = issue?.kind === 'mismatch' ? PASSWORDS_DONT_MATCH : undefined;
  const fieldsDisabled = !ready || saved;

  return (
    <AuthLayout>
      <AuthHeading
        title="Set a new password"
        description={linkInvalid ? undefined : 'Choose a new password for SBG Funding EMS. Once it’s saved, we’ll sign you in.'}
      />

      {(checkingLink || linkInvalid || issue?.kind === 'failed' || saved) && (
        <Box sx={{ mb: 3 }}>
          {checkingLink && (
            <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, color: sbg.text.secondary }}>
              <CircularProgress size={16} thickness={5} aria-hidden="true" />
              <Typography variant="body1" component="span">
                Checking your reset link…
              </Typography>
            </Box>
          )}
          {linkInvalid && (
            <Alert severity="error">
              <AlertTitle>{linkProblem === 'expired' ? LINK_INVALID : LINK_UNOPENABLE}</AlertTitle>
              {linkProblem === 'expired' && 'Reset links work once and expire after a while. '}
              Request a new one and we’ll email it to you.
            </Alert>
          )}
          {!linkInvalid && issue?.kind === 'failed' && (
            <Alert severity="error">
              <AlertTitle>Couldn’t save your new password</AlertTitle>
              Try again in a minute. If it keeps happening, request a new reset link.
            </Alert>
          )}
          {saved && (
            <Alert severity="success" role="status">
              {PASSWORD_SAVED}
            </Alert>
          )}
        </Box>
      )}

      {linkInvalid ? (
        // A dead link is a dead end: no fields, one way forward.
        <Button
          component={RouterLink}
          to="/login"
          state={{ openReset: true }}
          ref={requestLinkRef}
          variant="contained"
          size="large"
          fullWidth
        >
          Request a new link
        </Button>
      ) : (
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ display: 'grid', gap: 2.5 }}>
          {/* Lets password managers file the new password under the right account. */}
          {accountEmail && (
            <Box
              component="input"
              type="email"
              name="username"
              autoComplete="username"
              value={accountEmail}
              readOnly
              tabIndex={-1}
              aria-hidden="true"
              sx={visuallyHidden}
            />
          )}
          <PasswordField
            id="new-password"
            label="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            disabled={fieldsDisabled}
            error={Boolean(newPasswordError)}
            helperText={newPasswordError ?? PASSWORD_HINT}
            inputRef={passwordRef}
          />
          <PasswordField
            id="confirm-password"
            label="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            disabled={fieldsDisabled}
            error={Boolean(confirmError)}
            helperText={confirmError}
            inputRef={confirmRef}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            // While saving, aria-disabled (not `disabled`) keeps keyboard focus on the button.
            disabled={!ready || saved}
            aria-disabled={loading || undefined}
            aria-busy={loading || undefined}
            sx={{
              '&[aria-disabled="true"]': { bgcolor: sbg.action.primaryHover, color: sbg.text.onBrand, cursor: 'progress' },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" />
                <span>Saving…</span>
              </>
            ) : saved ? (
              'Password saved'
            ) : (
              'Save new password'
            )}
          </Button>
        </Box>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button component={RouterLink} to="/login" variant="text" startIcon={<ArrowLeft />}>
          Back to sign in
        </Button>
      </Box>
    </AuthLayout>
  );
};

export default ResetPassword;
